import { FC, useEffect, useState, useMemo, useContext, Fragment } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { UserContext, VendorPageContext } from '../../../context';
import { useMediaQuery } from '@mui/material';
import { Table, Pagination, Link } from '../../../components';
import { downloadACHNotificationPDF, getPayments } from '../../../fetch';
import { PaymentsFilters } from './PaymentsFilters';
import { useSnackbar } from 'notistack';
import { downloadFile, formatDate, formatMoney } from '../../../helpers';
import { PaymentsMobile } from './PaymentsMobile';
import { IInvoicePayment } from '../../../models';
import { VendorPage } from '../VendorPage';
import { InsertDriveFileOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';

interface IPayments {}

export const Payments: FC<IPayments> = () => {
  const classes = useStyles();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const { enqueueSnackbar } = useSnackbar();
  const { isAdmin } = useContext(UserContext);
  const { loadingVendors, vendorSageId } = useContext(VendorPageContext);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [isLoadingPayments, setLoadingPayments] = useState<boolean>(true);
  const [isDownloading, setDownloading] = useState<{
    [id: string | number]: boolean;
  }>({});
  const [payments, setPayments] = useState<IInvoicePayment[]>([]);
  const [selectedSort, setSelectedSort] = useState<string>('Date');
  const [sortDirection, setSortDirection] = useState<{
    Date?: 'Asc' | 'Desc';
    Number?: 'Asc' | 'Desc';
    Amount?: 'Asc' | 'Desc';
  }>({
    Date: 'Desc'
  });

  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState<{
    searchValue?: number | string;
  } | null>({});

  const fetchPayments = async () => {
    try {
      setLoadingPayments(true);
      const res = await getPayments({
        page: page + 1,
        perPage: perPage,
        query: searchValue,
        vendorId: vendorSageId,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string
      });
      setRecordCount(res.totalRecordCount);
      setPayments(res.records);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading payments, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };
  const handleDownloadPDF = async (batch: IInvoicePayment) => {
    setDownloading({
      [batch.batchNumber]: true
    });
    try {
      const res = await downloadACHNotificationPDF(batch.batchNumber, batch.vendor.vendorId);
      if (res?.Detail) {
        enqueueSnackbar(`Error, ${res.Detail}`, {
          variant: 'error'
        });
      } else if (res && res.type.includes('application/pdf')) {
        return downloadFile(res, `${batch.type} - ${batch.number}`);
      }
      enqueueSnackbar(`Error downloading pdf, please try again.`, {
        variant: 'error'
      });
    } catch (error) {
      enqueueSnackbar(`Error downloading pdf, please try again.`, {
        variant: 'error'
      });
    } finally {
      setDownloading({
        [batch.batchNumber]: false
      });
    }
  };
  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorSageId, filters, sortDirection, selectedSort, page, perPage]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Type',
        accessor: 'type',
        sort: false
      },
      {
        Header: 'Check No.',
        accessor: 'number',
        isServerSorted: selectedSort === 'Number',
        isServerSortedDesc: sortDirection.Number === 'Desc',
        handleClickColumn: () => handleClickColumn('Number'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IInvoicePayment } };
        }) => {
          return (
            <Link
              to={`/vendors/payments/${original.number}?type=${original.type.replace(
                ' ',
                ''
              )}&vendorId=${original.vendor.vendorId}`}
            >
              {original.number}
            </Link>
          );
        }
      },
      {
        Header: 'Date',
        accessor: 'date',
        isServerSorted: selectedSort === 'Date',
        isServerSortedDesc: sortDirection.Date === 'Desc',
        handleClickColumn: () => handleClickColumn('Date'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IInvoicePayment } };
        }) => {
          return <span>{formatDate(original.date)}</span>;
        }
      },
      isAdmin && {
        Header: 'Vendor',
        accessor: 'vendor.name',
        sort: false
      },
      isAdmin && {
        Header: 'Subcontractor Type',
        accessor: 'vendor.subType',
        sort: false
      },
      {
        Header: 'Lot(s)',
        accessor: 'lotNumbers',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IInvoicePayment } };
        }) => {
          const lotNumbers = original.lotNumbers.map((lotNumber, index) => {
            const seperator = index !== 0 ? ', ' : '';
            return (
              <Fragment key={index}>
                {seperator}
                {lotNumber}
              </Fragment>
            );
          });
          return <span>{lotNumbers}</span>;
        }
      },
      {
        Header: 'Total',
        accessor: 'amount',
        isServerSorted: selectedSort === 'Amount',
        isServerSortedDesc: sortDirection.Amount === 'Desc',
        handleClickColumn: () => handleClickColumn('Amount'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IInvoicePayment } };
        }) => {
          return <span>{formatMoney(original.amount)}</span>;
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IInvoicePayment } };
        }) => {
          return (
            <div>
              {!!original?.hasAchSummaryGenerated && (
                <LoadingButton
                  loading={isDownloading[original.batchNumber]}
                  loadingPosition="start"
                  color="primary"
                  startIcon={<InsertDriveFileOutlined />}
                  disabled={isDownloading[original.batchNumber]}
                  className={classes.actionButton}
                  onClick={() => handleDownloadPDF(original)}
                >
                  PDF
                </LoadingButton>
              )}
            </div>
          );
        }
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments, isDownloading]);

  return (
    <VendorPage>
      <PaymentsFilters
        isLoading={isLoadingPayments || loadingVendors}
        handleSearch={(clearSearch?: boolean) => {
          setPage(0);
          setFilters({
            searchValue: searchValue
          });
          if (clearSearch) {
            setSearchValue('');
          }
        }}
        applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
          if (clearFilters) {
            setFilters({
              searchValue: ''
            });
          } else {
            setPage(0);
            setFilters({
              ...filters,
              searchValue: searchValue || undefined
            });
          }
        }}
        setSearchValue={setSearchValue}
        searchValue={searchValue}
      />
      <Table
        columns={columns}
        data={payments}
        isLoading={isLoadingPayments || loadingVendors}
        hidePagination
        ResponsiveComponent={isMobile ? PaymentsMobile : undefined}
        mobileProps={{ isAdmin: isAdmin, handleDownloadPDF, isDownloading }}
      />
      {!isLoadingPayments && !loadingVendors && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
    </VendorPage>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  actionButton: {
    textAlign: 'right',
    fontSize: 13,
    '&& + &&': {
      marginLeft: theme.spacing(0.5)
    },
    '&& .MuiButton-startIcon': {
      position: 'relative',
      top: '-2px'
    }
  }
}));
